import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { ProductSchedule } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { useScheduleFormat } from '../hooks/use-schedule-format';
import { useScheduleRuns } from '../hooks/use-schedule-runs';

import { RunCounts } from './run-counts';
import { RunDetailSheet } from './run-detail-sheet';
import { RunStatusBadge } from './schedule-status-badge';

const ROUTE_ID = '/_authed/_active/org/schedules/$scheduleId' as const;
const ROUTE_PATH = '/org/schedules/$scheduleId' as const;

/** Every run, newest first; a row opens its items (`?run=` keeps it linkable from the bell). */
export function RunsTab({ schedule }: { schedule: ProductSchedule }) {
  const { t } = useTranslation('schedules');
  const relative = useFormat();
  const format = useScheduleFormat(schedule.timeZone);
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const runs = useScheduleRuns(schedule.id, schedule.runningRunId !== null);
  const rows = runs.data?.pages.flatMap((page) => page.items) ?? [];
  const open = (runId: string | undefined) =>
    void navigate({ search: (prev) => ({ ...prev, tab: 'runs', run: runId }) });

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">{t('runs.intro')}</p>
      {runs.isError ? (
        <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />
      ) : runs.isPending ? (
        <div className="flex flex-col gap-2" aria-busy>
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground rounded-md border p-6 text-center text-sm">
          {t('runs.empty')}
        </p>
      ) : (
        <div className="rounded-md border" data-tour="schedules.detail.runs-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('runs.columns.run')}</TableHead>
                <TableHead>{t('runs.columns.status')}</TableHead>
                <TableHead>{t('runs.columns.when')}</TableHead>
                <TableHead>{t('runs.columns.counts')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((run) => {
                const at = run.startedAt ?? run.scheduledFor ?? run.createdAt;
                return (
                  <TableRow
                    key={run.id}
                    className="cursor-pointer"
                    onClick={() => open(run.id)}
                    data-state={search.run === run.id ? 'selected' : undefined}
                  >
                    <TableCell>
                      <button
                        type="button"
                        className="font-medium hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          open(run.id);
                        }}
                      >
                        {run.cycle === null
                          ? t(`runKind.${run.kind}`)
                          : t('runs.titleCycle', {
                              kind: t(`runKind.${run.kind}`),
                              cycle: run.cycle,
                            })}
                      </button>
                      {run.error ? (
                        <span className="text-destructive block max-w-80 truncate text-xs">
                          {run.error}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <RunStatusBadge status={run.status} />
                    </TableCell>
                    <TableCell title={format.utc(at)}>
                      {format.dateTime(at)}
                      <span className="text-muted-foreground block text-xs">
                        {relative.relative(at)}
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-normal">
                      <RunCounts counts={run.counts} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {runs.hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          className="self-start"
          disabled={runs.isFetchingNextPage}
          onClick={() => void runs.fetchNextPage()}
        >
          {t('runs.loadMore')}
        </Button>
      ) : null}
      <RunDetailSheet
        key={search.run ?? 'closed'}
        schedule={schedule}
        runId={search.run ?? null}
        onClose={() => open(undefined)}
      />
    </div>
  );
}
